import React, { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, Send, X, Minimize2 } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { ScrollArea } from "@/shared/components/ui/scroll-area";
import { apiClient } from "@/shared/services/api";

interface Message {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
}

interface ChatSession {
  messages: Message[];
  startTime: number;
}

export function AIChatbot() {
  const location = useLocation();
  
  // Hide chatbot on video call routes - must be before other hooks
  const shouldHideChatbot = location.pathname.includes('/video-call') || 
                           location.pathname.includes('/call/') ||
                           location.pathname.startsWith('/call/') ||
                           location.pathname === '/video-call';

  if (shouldHideChatbot) {
    return null;
  }

  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Load messages from localStorage with 24-hour expiry
  useEffect(() => {
    const loadMessages = () => {
      try {
        const stored = localStorage.getItem('ai-chatbot-session');
        if (stored) {
          const session: ChatSession = JSON.parse(stored);
          const now = Date.now();
          const hoursSinceStart = (now - session.startTime) / (1000 * 60 * 60);
          
          if (hoursSinceStart < 24) {
            // Session is still valid, restore messages
            const messagesWithDates = session.messages.map(msg => ({
              ...msg,
              timestamp: new Date(msg.timestamp)
            }));
            setMessages(messagesWithDates);
            return;
          } else {
            // Session expired, clear it
            localStorage.removeItem('ai-chatbot-session');
          }
        }
        
        // Start new session with welcome message
        const welcomeMessage: Message = {
          id: "welcome",
          text: "Hi! I'm your Ask for Help assistant. I can help you with questions about the platform, calls, projects, and more. How can I help you today?",
          isUser: false,
          timestamp: new Date()
        };
        
        const newSession: ChatSession = {
          messages: [welcomeMessage],
          startTime: Date.now()
        };
        
        setMessages([welcomeMessage]);
        localStorage.setItem('ai-chatbot-session', JSON.stringify(newSession));
      } catch (error) {
        console.error('Error loading chat session:', error);
        // Fallback to welcome message
        const welcomeMessage: Message = {
          id: "welcome",
          text: "Hi! I'm your Ask for Help assistant. I can help you with questions about the platform, calls, projects, and more. How can I help you today?",
          isUser: false,
          timestamp: new Date()
        };
        setMessages([welcomeMessage]);
      }
    };

    loadMessages();
  }, []);

  // Save messages to localStorage whenever messages change
  useEffect(() => {
    if (messages.length > 0) {
      try {
        const stored = localStorage.getItem('ai-chatbot-session');
        const session: ChatSession = stored ? JSON.parse(stored) : { messages: [], startTime: Date.now() };
        
        const updatedSession: ChatSession = {
          ...session,
          messages: messages
        };
        
        localStorage.setItem('ai-chatbot-session', JSON.stringify(updatedSession));
      } catch (error) {
        console.error('Error saving chat session:', error);
      }
    }
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const getSmartResponse = (userMessage: string): string => {
    const message = userMessage.toLowerCase();
    
    // Password reset
    if (message.includes('password') && (message.includes('reset') || message.includes('change') || message.includes('forgot'))) {
      return "To change your password, go to your Profile page and click on the Security tab where you'll find the 'Change Password' option. [Go to Profile - Security](/profile)";
    }
    
    // Phone number updates
    if (message.includes('phone') && (message.includes('update') || message.includes('change') || message.includes('number'))) {
      return "To update your phone number, go to your Profile page under the Personal Details tab where you can edit your contact information. [Go to Profile - Personal Details](/profile)";
    }
    
    // Email updates
    if (message.includes('email') && (message.includes('update') || message.includes('change'))) {
      return "To update your email address, go to your Profile page under the Personal Details tab where you can edit your contact information. [Go to Profile - Personal Details](/profile)";
    }
    
    // Billing and payment management
    if (message.includes('billing') || message.includes('payment') || message.includes('invoice') || message.includes('subscription') || message.includes('upgrade') || message.includes('downgrade')) {
      return "For billing information, visit your Profile page and click on the Billing tab to view invoices, payment methods, subscription details, and manage your account billing. [Go to Profile - Billing](/profile?tab=billing)";
    }
    
    // Cancellation
    if (message.includes('cancel') && (message.includes('subscription') || message.includes('account') || message.includes('service'))) {
      return "To cancel your subscription, go to your Profile page and click on the Billing tab where you'll find subscription management options. [Go to Profile - Billing](/profile)";
    }
    
    // Notifications management
    if (message.includes('notification') || message.includes('alerts') || message.includes('emails') || message.includes('communication')) {
      return "To manage your notification preferences, go to your Profile page and click on the Notifications tab where you can control email alerts, communication preferences, and notification settings. [Go to Profile - Notifications](/profile)";
    }
    
    // Security settings
    if (message.includes('security') || message.includes('two-factor') || message.includes('2fa') || message.includes('login')) {
      return "For security settings including two-factor authentication and login notifications, go to your Profile page and click on the Security tab. [Go to Profile - Security](/profile)";
    }
    
    // Scheduling calls
    if (message.includes('schedule') || message.includes('book') || message.includes('appointment') || message.includes('call')) {
      return "You can schedule calls from the Calls page where you can create new appointments, view upcoming calls, and manage your call history. [Go to Calls](/calls)";
    }
    
    // Referrals
    if (message.includes('refer') || message.includes('friend') || message.includes('invite')) {
      return "Use the Refer a Friend feature to invite colleagues and earn rewards. You can track your referrals and see your earning progress. [Refer a Friend](/refer-friend)";
    }
    
    // Profile/Account general
    if (message.includes('profile') || message.includes('account') || message.includes('personal')) {
      return "You can manage your profile information, contact details, billing, security, and notifications from your Profile page. It has tabs for Personal Details, Billing, Security, and Notifications. [Go to Profile](/profile)";
    }
    
    // Settings (app preferences)
    if (message.includes('settings') || message.includes('preferences') || message.includes('organization')) {
      return "You can manage your application settings, call preferences, virtual backgrounds, and organization settings from the Settings page. [Go to Settings](/settings)";
    }
    
    // Projects
    if (message.includes('project') || message.includes('manage')) {
      return "Visit the Projects page to create, edit, and manage your projects. You can track project status and organize your work. [Go to Projects](/projects)";
    }
    
    // Users/Team
    if (message.includes('user') || message.includes('team') || message.includes('member')) {
      return "Manage your team members and user access levels from the Users page. You can add, edit, and remove users from your organization. [Go to Users](/users)";
    }
    
    // Support
    if (message.includes('support') || message.includes('help') || message.includes('contact')) {
      return "For additional help, visit our Support Center where you can submit tickets, browse FAQs, and get phone support. [Go to Support](/support)";
    }
    
    // Reports
    if (message.includes('report') || message.includes('analytics') || message.includes('data')) {
      return "View your analytics and reports from the Reports page to track your usage, performance metrics, and business insights. [Go to Reports](/reports)";
    }
    
    // Contacts
    if (message.includes('contact') || message.includes('customer') || message.includes('client')) {
      return "Manage your contacts and customer information from the Contacts page where you can add, edit, and organize your customer database. [Go to Contacts](/contacts)";
    }
    
    // Apps/Integrations
    if (message.includes('app') || message.includes('integration') || message.includes('connect')) {
      return "Manage your app integrations and connected services from the Apps page where you can configure various platform integrations. [Go to Apps](/apps)";
    }
    
    // Default response
    return "I can help you navigate the platform! I know where to find:\n• Password changes\n• Billing updates\n• Phone number changes\n• Notification settings\n• Security options\n• Call scheduling\n• And much more!\n\nWhat specific feature are you looking for? You can also visit our [Support Center](/support) for detailed help.";
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputValue,
      isUser: true,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const currentInput = inputValue;
    setInputValue("");
    setIsLoading(true);

    try {
      // First try to get a smart response
      const smartResponse = getSmartResponse(currentInput);
      
      // If we have a smart response, use it
      if (smartResponse) {
        const aiMessage: Message = {
          id: (Date.now() + 1).toString(),
          text: smartResponse,
          isUser: false,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, aiMessage]);
        setIsLoading(false);
        return;
      }

      // SECURITY: Use backend proxy instead of calling edge function directly
      const data = await apiClient.post<{ response?: string }>('/edge/ai-assistant', {
        message: currentInput
      });

      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: data.response || "I'm sorry, I couldn't process your request at the moment.",
        isUser: false,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: "I'm sorry, I'm having trouble connecting right now. Please try again later.",
        isUser: false,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {/* Chat Window */}
      {isOpen && (
        <div className="mb-4 w-80 h-96 bg-card border border-border rounded-lg shadow-lg flex flex-col animate-in slide-in-from-bottom-2">
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-border">
            <div className="flex items-center space-x-2">
              <img 
                src="/lovable-uploads/3b39a7ae-d9ab-4bb2-b53e-362e23c5e771.png" 
                alt="walkie logo" 
                className="h-5 w-5 rounded-full object-cover object-center"
                style={{ objectPosition: 'center', transform: 'scale(1.3)' }}
              />
              <span className="font-medium text-sm">Ask for Help!</span>
            </div>
            <div className="flex items-center space-x-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsOpen(false)}
                className="h-6 w-6 p-0"
              >
                <Minimize2 className="h-3 w-3" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsOpen(false)}
                className="h-6 w-6 p-0"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          </div>

          {/* Messages */}
          <ScrollArea className="flex-1 p-3">
            <div className="space-y-3">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.isUser ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-lg p-2 text-xs ${
                      message.isUser
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    <div className="text-sm text-gray-700 whitespace-pre-wrap">
                      {message.text.split(/(\[.*?\]\(.*?\))/g).map((part, index) => {
                        const linkMatch = part.match(/\[(.*?)\]\((.*?)\)/);
                        if (linkMatch) {
                          const [, linkText, linkUrl] = linkMatch;
                          return (
                            <a
                              key={index}
                              href={linkUrl}
                              className="text-primary hover:underline font-medium"
                              onClick={(e) => {
                                e.preventDefault();
                                window.location.href = linkUrl;
                              }}
                            >
                              {linkText}
                            </a>
                          );
                        }
                        return part;
                      })}
                    </div>
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-muted text-muted-foreground rounded-lg p-2 text-xs">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-current rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-current rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-current rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div ref={messagesEndRef} />
          </ScrollArea>

          {/* Input */}
          <div className="p-3 border-t border-border">
            <div className="flex space-x-2">
              <Input
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask me anything..."
                className="text-xs"
                disabled={isLoading}
                autoCorrect="on"
                spellCheck="true"
              />
              <Button
                size="sm"
                onClick={handleSendMessage}
                disabled={isLoading || !inputValue.trim()}
                className="px-2"
              >
                <Send className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Chat Button */}
      <Button
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={`rounded-full transition-all duration-300 shadow-lg ${
          isHovered || isOpen 
            ? 'w-32 px-4' 
            : 'w-12 h-12 p-0'
        }`}
        size={isHovered || isOpen ? "default" : "icon"}
      >
        <img 
          src="/lovable-uploads/3b39a7ae-d9ab-4bb2-b53e-362e23c5e771.png" 
          alt="walkie logo" 
          className={`h-6 w-6 rounded-full object-cover object-center ${isHovered || isOpen ? 'mr-2' : ''}`}
          style={{ objectPosition: 'center', transform: 'scale(1.3)' }}
        />
        {(isHovered || isOpen) && (
          <span className="text-sm font-medium">
            {isOpen ? 'Close' : 'Ask for Help!'}
          </span>
        )}
      </Button>
    </div>
  );
}